/**
 * Parameter-list parsing for the parked-routine parser (VDE-2 Slice 4):
 * PG / MySQL / Oracle `[mode] name type [DEFAULT|=expr]` parser plus the
 * top-level `parseParamList` orchestrator (which tries the MSSQL `@param`
 * branch first via `routine.parse.params.mssql.ts`). Split out of
 * `routine.parse.ts` (ROUND2-MINOR-14) to keep each module under the
 * 200-line file budget while preserving the byte-faithful contract
 * documented on `routine.parse.ts`.
 *
 * Regex-free (project rule VQE-D10). The PG/MySQL/Oracle matcher mirrors the
 * removed parser's mode-prefix alternation (`IN OUT` → `INOUT` → `IN` → `OUT`,
 * with a trailing `\s+`), the `nameMatch` (bare ident OR `"…"` OR `` `…` ``;
 * NO `[ ]` form; trailing `\s+`), the leftmost word-bounded `\bDEFAULT\s+`
 * scan and the leftmost ` = ` (`\s=\s`) separator scan.
 */

import { isIdentStart, isWordChar, isWs, strTrim, unquoteIdent } from './routine.parse.lex';
import { splitTopLevelCommas } from './routine.parse.name';
import { parseMssqlParam } from './routine.parse.params.mssql';
import type { ParsedParkedRoutineParam } from './routineTypes';

/** `^(IN\s+OUT|INOUT|IN|OUT)\s+` anchored at `from` — replicates the removed
 *  parser's mode regex (NOT word-bounded; alternation tries `IN OUT`, then
 *  `INOUT`, `IN`, `OUT`; requires a trailing `\s+`). */
function matchModePrefix(s: string, from: number): { end: number; mode: 'in' | 'out' | 'inout' } | null {
  const up = s.toUpperCase();
  if (up.startsWith('IN', from)) {
    const p = from + 2;
    let q = p;
    while (q < s.length && isWs(s[q])) q++;
    if (q > p && up.startsWith('OUT', q)) {
      const e = q + 3;
      let r = e;
      while (r < s.length && isWs(s[r])) r++;
      if (r > e) return { end: r, mode: 'inout' };
    }
  }
  if (up.startsWith('INOUT', from)) {
    const e = from + 5;
    let r = e;
    while (r < s.length && isWs(s[r])) r++;
    if (r > e) return { end: r, mode: 'inout' };
  }
  if (up.startsWith('IN', from)) {
    const e = from + 2;
    let r = e;
    while (r < s.length && isWs(s[r])) r++;
    if (r > e) return { end: r, mode: 'in' };
  }
  if (up.startsWith('OUT', from)) {
    const e = from + 3;
    let r = e;
    while (r < s.length && isWs(s[r])) r++;
    if (r > e) return { end: r, mode: 'out' };
  }
  return null;
}

/** `^([A-Za-z_][A-Za-z0-9_]*|"[^"]+"|`[^`]+`)\s+` anchored at `from` — the
 *  removed parser's `nameMatch` (note: NO `[ ]` form here, and a trailing
 *  `\s+` is required). Returns the raw token + the index past the `\s+`. */
function matchParamName(s: string, from: number): { raw: string; end: number } | null {
  const c = s[from];
  let rawEnd = -1;
  if (c === '"') {
    let j = from + 1;
    while (j < s.length && s[j] !== '"') j++;
    if (j >= s.length || j === from + 1) return null;
    rawEnd = j + 1;
  } else if (c === '`') {
    let j = from + 1;
    while (j < s.length && s[j] !== '`') j++;
    if (j >= s.length || j === from + 1) return null;
    rawEnd = j + 1;
  } else if (isIdentStart(c)) {
    let j = from + 1;
    while (j < s.length && isWordChar(s[j])) j++;
    rawEnd = j;
  } else {
    return null;
  }
  let r = rawEnd;
  while (r < s.length && isWs(s[r])) r++;
  if (r === rawEnd) return null; // \s+
  return { raw: s.slice(from, rawEnd), end: r };
}

/** Leftmost word-bounded `DEFAULT` followed by `\s+` (the removed parser's
 *  `/\bDEFAULT\s+([\s\S]*)$/i`). Returns the match start (`.index`) + the
 *  capture start (past `DEFAULT\s+`), or null. */
function findDefaultKeyword(t: string): { index: number; capStart: number } | null {
  for (let i = 0; i + 7 <= t.length; i++) {
    if (t.slice(i, i + 7).toUpperCase() !== 'DEFAULT') continue;
    if (i > 0 && isWordChar(t[i - 1])) continue; // \b
    const p = i + 7;
    let q = p;
    while (q < t.length && isWs(t[q])) q++;
    if (q === p) continue; // \s+
    return { index: i, capStart: q };
  }
  return null;
}

/** Leftmost ` = ` (`/\s=\s/` — exactly one ws char each side). Returns the
 *  match start, or `-1`. The matched span length is always 3. */
function findEqualsSep(t: string): number {
  for (let i = 0; i + 3 <= t.length; i++) {
    if (isWs(t[i]) && t[i + 1] === '=' && isWs(t[i + 2])) return i;
  }
  return -1;
}

function parseParamEntry(s: string): ParsedParkedRoutineParam {
  const mssql = parseMssqlParam(s);
  if (mssql) return mssql;

  let cursor = 0;
  let mode: 'in' | 'out' | 'inout' | undefined;
  const front = matchModePrefix(s, 0);
  if (front) { mode = front.mode; cursor = front.end; }

  const nameRead = matchParamName(s, cursor);
  if (!nameRead) return { name: strTrim(s), typeText: '' };
  const name = unquoteIdent(nameRead.raw).name;
  cursor = nameRead.end;

  if (mode === undefined) {
    const mid = matchModePrefix(s, cursor);
    if (mid) { mode = mid.mode; cursor = mid.end; }
  }

  const tail = s.slice(cursor);
  const def = findDefaultKeyword(tail);
  let typeText = strTrim(tail);
  let defaultExpr: string | undefined;
  if (def) {
    typeText = strTrim(tail.slice(0, def.index));
    defaultExpr = strTrim(tail.slice(def.capStart));
  } else {
    const eq = findEqualsSep(tail);
    if (eq !== -1) {
      typeText = strTrim(tail.slice(0, eq));
      defaultExpr = strTrim(tail.slice(eq + 3));
    }
  }
  return {
    name,
    typeText,
    ...(mode ? { mode } : {}),
    ...(defaultExpr ? { defaultExpr } : {}),
  };
}

/** Parse the inner text of the parameter `(...)` — split at top-level commas
 *  then per-segment parse, dropping empty segments. Faithful to the removed
 *  parser's `parseParamList`. */
export function parseParamList(slice: string): ReadonlyArray<ParsedParkedRoutineParam> {
  const inner = strTrim(slice);
  if (inner.length === 0) return [];
  const params: ParsedParkedRoutineParam[] = [];
  for (const raw of splitTopLevelCommas(inner)) {
    const trimmed = strTrim(raw);
    if (trimmed.length === 0) continue;
    params.push(parseParamEntry(trimmed));
  }
  return params;
}
